import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, Collapse, Divider, Link } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';

import { getCookieConsent, setCookieConsent } from '../../../services/consent/cookieConsent';

const cookieTypes = [
  {
    label: 'Essential Cookies',
    desc: 'These cookies are necessary for the website to function properly and cannot be disabled. They are usually set in response to actions you take that constitute a request for services, such as setting your privacy preferences, logging in, or filling out forms.',
    required: true,
  },
  {
    label: 'Performance Cookies',
    desc: 'These cookies are necessary for the website to function properly and cannot be disabled. They are usually set in response to actions you take that constitute a request for services, such as setting your privacy preferences, logging in, or filling out forms.',
    required: false,
  },
];

const CookieBanner: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [performance, setPerformance] = useState(false);

  useEffect(() => {
    const consent = getCookieConsent();
    if (!consent) {
      setVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    setCookieConsent('all');
    setVisible(false);
  };

  const handleSavePreferences = () => {
    setCookieConsent(performance ? 'all' : 'essential');
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <>
      {/* Backdrop */}
      <Box
        sx={{
          position: 'fixed',
          inset: 0,
          bgcolor: 'rgba(0,0,0,0.4)',
          zIndex: 9998,
        }}
      />

      {/* Modal */}
      <Box
        sx={{
          position: 'fixed',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          zIndex: 9999,
          width: 520,
          maxWidth: 'calc(100vw - 48px)',
          maxHeight: '90vh',
          overflowY: 'auto',
          bgcolor: '#fff',
          border: '1px solid #ddd',
          p: 4,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
          Cookies on sirivannavari.com
        </Typography>

        <Typography variant="body2" sx={{ color: '#333', lineHeight: 1.7, mb: 2.5 }}>
          Sirivannavari uses cookies to deliver content and provide an experience tailored to
          your personal preferences. You can manage your settings according to your preferences
          and access more information at any time by clicking on "Cookie Settings" and reviewing
          the{' '}
          <Link href="/privacy" sx={{ color: '#000', fontWeight: 700 }}>
            Privacy Policy
          </Link>
          .
        </Typography>

        <Button
          variant="contained"
          onClick={handleAcceptAll}
          sx={{
            bgcolor: '#000',
            color: '#fff',
            borderRadius: 0,
            px: 4,
            py: 1.2,
            mb: 2,
            fontWeight: 600,
            fontSize: '0.95rem',
            textTransform: 'none',
            '&:hover': { bgcolor: '#222' },
          }}
        >
          Accept all
        </Button>

        <Divider />

        <Button
          onClick={() => setExpanded((prev) => !prev)}
          endIcon={expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          sx={{
            color: '#000',
            fontWeight: 600,
            fontSize: '0.9rem',
            p: 0,
            mt: 1.5,
            textTransform: 'none',
            '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
          }}
          disableRipple
        >
          Setting cookies
        </Button>

        <Collapse in={expanded}>
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 2 }}>
              Select Cookie Type
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
              {cookieTypes.map((item) => {
                const checked = item.required || performance;
                const isPerformance = !item.required;
                return (
                  <Box
                    key={item.label}
                    sx={{ display: 'flex', gap: 2, cursor: isPerformance ? 'pointer' : 'default' }}
                    onClick={() => isPerformance && setPerformance((p) => !p)}
                  >
                    <Box sx={{ mt: 0.3, flexShrink: 0 }}>
                      {checked ? (
                        <CheckCircleIcon sx={{ fontSize: 24, color: item.required ? '#555' : '#000' }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 24, color: '#999' }} />
                      )}
                    </Box>
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: item.required ? '#999' : '#000', mb: 0.5 }}
                      >
                        {item.label}
                      </Typography>
                      <Typography variant="body2" sx={{ color: '#555', lineHeight: 1.6 }}>
                        {item.desc}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>

            <Button
              variant="outlined"
              onClick={handleSavePreferences}
              sx={{
                mt: 3,
                color: '#000',
                borderColor: '#000',
                borderRadius: 0,
                fontWeight: 600,
                fontSize: '0.9rem',
                textTransform: 'none',
                px: 4,
                py: 1,
                '&:hover': { bgcolor: '#f5f5f5', borderColor: '#000' },
              }}
            >
              {performance ? 'Save preferences' : 'Accept essential cookies'}
            </Button>
          </Box>
        </Collapse>
      </Box>
    </>
  );
};

export default CookieBanner;
